import { useState, useRef, ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: string | ReactNode;
  children: ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  variant?: "success" | "error" | "info";
}

export default function Tooltip({
  content,
  children,
  placement = "top",
  variant = "info",
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      let x = rect.left + rect.width / 2;
      let y = rect.top;

      switch (placement) {
        case "top":
          y = rect.top - 8;
          break;
        case "bottom":
          y = rect.bottom + 8;
          break;
        case "left":
          x = rect.left - 8;
          y = rect.top + rect.height / 2;
          break;
        case "right":
          x = rect.right + 8;
          y = rect.top + rect.height / 2;
          break;
      }

      setPosition({ x, y });
    }
  }, [isVisible, placement]);

  const getTooltipClasses = () => {
    switch (placement) {
      case "top":
        return "transform -translate-x-1/2 -translate-y-full";
      case "bottom":
        return "transform -translate-x-1/2";
      case "left":
        return "transform -translate-x-full -translate-y-1/2";
      case "right":
        return "transform -translate-y-1/2";
      default:
        return "transform -translate-x-1/2";
    }
  };

  const getTooltipStyles = () => {
    switch (variant) {
      case "error":
        return "px-4 py-3 text-sm font-medium text-gray-800 bg-red-50 border border-red-500 rounded-lg shadow-lg whitespace-pre-wrap max-w-xs pointer-events-none";
      case "success":
        return "px-4 py-3 text-sm font-medium text-gray-800 bg-green-50 border border-green-500 rounded-lg shadow-lg whitespace-pre-wrap max-w-xs pointer-events-none";
      case "info":
      default:
        return "px-4 py-3 text-sm text-black bg-gray-50 border border-gray-200 rounded-lg shadow-md whitespace-pre-wrap max-w-xs pointer-events-none";
    }
  };

  const getArrowClasses = () => {
    let borderColor: string;
    switch (variant) {
      case "error":
        borderColor = "red-500";
        break;
      case "success":
        borderColor = "green-500";
        break;
      case "info":
      default:
        borderColor = "gray-400";
        break;
    }

    switch (placement) {
      case "top":
        return `absolute top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-${borderColor}`;
      case "bottom":
        return `absolute bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-${borderColor}`;
      case "left":
        return `absolute left-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-${borderColor}`;
      case "right":
        return `absolute right-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-${borderColor}`;
      default:
        return `absolute bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-${borderColor}`;
    }
  };

  const tooltip = isVisible ? (
    <div
      className={`fixed z-[9999] ${getTooltipStyles()} ${getTooltipClasses()}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {content}
      <div className={getArrowClasses()} />
    </div>
  ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {tooltip && createPortal(tooltip, document.body)}
    </>
  );
}
